import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

const CookiePolicy = () => {
  const scriptContainerRef = useRef(null);

  useEffect(() => {
    // Check if script is already loaded
    if (document.getElementById('cky-cookie-policy')) {
      // Script already exists, check if content is loaded
      const checkContent = () => {
        const policyContainer = document.getElementById('cky-policy-container');
        if (policyContainer) {
          hidePlaceholder();
          moveContentToArea();
        }
      };
      checkContent();
      const interval = setInterval(checkContent, 500);
      return () => clearInterval(interval);
    }

    // Wait for container to be available
    if (!scriptContainerRef.current) return;

    // Create and insert script
    const script = document.createElement('script');
    script.id = 'cky-cookie-policy';
    script.type = 'text/javascript';
    script.src = 'https://cdn-cookieyes.com/client_data/55432f87b7c0f4252489e7b6b75acc39/cookie-policy/script.js';
    script.async = true;

    // Insert script into container
    scriptContainerRef.current.appendChild(script);

    // Monitor for content insertion
    const observer = new MutationObserver(() => {
      const policyContainer = document.getElementById('cky-policy-container');
      if (policyContainer) {
        hidePlaceholder();
        moveContentToArea();
        observer.disconnect();
      }
    });

    // Observe the container and body
    if (scriptContainerRef.current) {
      observer.observe(scriptContainerRef.current, {
        childList: true,
        subtree: true
      });
    }
    observer.observe(document.body, {
      childList: true,
      subtree: true
    });

    // Fallback: check periodically
    const checkInterval = setInterval(() => {
      const policyContainer = document.getElementById('cky-policy-container');
      if (policyContainer) {
        hidePlaceholder();
        moveContentToArea();
        clearInterval(checkInterval);
        observer.disconnect();
      }
    }, 500);

    // Cleanup
    return () => {
      observer.disconnect();
      clearInterval(checkInterval);
    };
  }, []);

  const hidePlaceholder = () => {
    const placeholder = document.getElementById('cookie-policy-placeholder');
    if (placeholder) {
      placeholder.style.display = 'none';
    }
  };

  const moveContentToArea = () => {
    const policyContainer = document.getElementById('cky-policy-container');
    const contentArea = document.getElementById('cookie-policy-content-area');
    if (policyContainer && contentArea && policyContainer.parentElement !== contentArea) {
      contentArea.appendChild(policyContainer);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 py-12">
        {/* Header */}
        <div className="mb-8">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors mb-6"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>Torna alla home</span>
          </Link>
          <h1 className="text-4xl font-light tracking-tight text-gray-900 mb-4">
            Cookie Policy
          </h1>
          <p className="text-gray-500 text-lg">
            Informativa sull'utilizzo dei cookie
          </p>
        </div>

        {/* Cookie Policy Content */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8">
          <div className="prose prose-gray max-w-none cookie-policy-wrapper">
            {/* Container for script - CookieYes will insert content after this script */}
            <div ref={scriptContainerRef} style={{ display: 'none' }}></div>
            
            {/* Container where CookieYes content will be displayed */}
            <div id="cookie-policy-content-area" className="cookie-policy-content"></div>
            
            {/* Placeholder message - will be hidden when content loads */}
            <div id="cookie-policy-placeholder">
              <p className="text-gray-600 mb-4">
                The cookie policy is automatically loaded via CookieYes.
              </p>
              <p className="text-sm text-gray-500">
                If the content does not appear, ensure that JavaScript is enabled in your browser.
              </p>
            </div>
          </div>
        </div>

        <style>{`
          .cookie-policy-wrapper {
            line-height: 1.75;
          }
          
          .cookie-policy-content h1,
          .cookie-policy-content .cookie-policy-h1 {
            font-size: 2rem;
            font-weight: 700;
            color: #111827;
            margin-top: 0;
            margin-bottom: 1.5rem;
            line-height: 1.2;
          }
          
          .cookie-policy-content h2 {
            font-size: 1.5rem;
            font-weight: 600;
            color: #374151;
            margin-top: 2rem;
            margin-bottom: 1rem;
            line-height: 1.3;
          }
          
          .cookie-policy-content h3 {
            font-size: 1.25rem;
            font-weight: 600;
            color: #4b5563;
            margin-top: 1.5rem;
            margin-bottom: 0.75rem;
          }
          
          .cookie-policy-content p,
          .cookie-policy-content .cookie-policy-p {
            color: #4b5563;
            margin-bottom: 1rem;
            line-height: 1.75;
          }
          
          .cookie-policy-content ul,
          .cookie-policy-content ol {
            margin-top: 1rem;
            margin-bottom: 1rem;
            padding-left: 1.75rem;
            color: #4b5563;
          }
          
          .cookie-policy-content li {
            margin-bottom: 0.5rem;
            line-height: 1.75;
          }
          
          .cookie-policy-content a {
            color: #2563eb;
            text-decoration: underline;
            transition: color 0.2s;
          }
          
          .cookie-policy-content a:hover {
            color: #1d4ed8;
          }
          
          .cookie-policy-content table {
            width: 100%;
            border-collapse: collapse;
            margin-top: 1.5rem;
            margin-bottom: 1.5rem;
          }
          
          .cookie-policy-content table th,
          .cookie-policy-content table td {
            padding: 0.75rem;
            border: 1px solid #e5e7eb;
            text-align: left;
          }
          
          .cookie-policy-content table th {
            background-color: #f9fafb;
            font-weight: 600;
            color: #111827;
          }
          
          .cookie-policy-content .cookie-policy-date-container {
            margin-bottom: 2rem;
            padding-bottom: 1rem;
            border-bottom: 1px solid #e5e7eb;
          }
          
          .cookie-policy-content .cookie-policy-date-container p {
            margin-bottom: 0.5rem;
            color: #6b7280;
            font-size: 0.875rem;
          }
          
          .cookie-policy-content .cky-banner-element {
            display: inline-block;
            padding: 0.5rem 1.5rem;
            background-color: #f8f9fa;
            color: #858a8f;
            border: 1px solid #dee2e6;
            border-radius: 0.25rem;
            cursor: pointer;
            text-decoration: none;
            font-weight: 500;
            margin-bottom: 1rem;
            transition: all 0.2s;
          }
          
          .cookie-policy-content .cky-banner-element:hover {
            background-color: #e9ecef;
            border-color: #adb5bd;
            color: #495057;
          }
          
          .cookie-policy-content .cky-audit-table-element {
            margin-top: 1rem;
            margin-bottom: 1rem;
          }
          
          .cookie-policy-content strong {
            font-weight: 600;
            color: #111827;
          }
          
          .cookie-policy-content code {
            background-color: #f3f4f6;
            padding: 0.125rem 0.375rem;
            border-radius: 0.25rem;
            font-size: 0.875em;
            color: #dc2626;
          }
        `}</style>
      </div>
    </div>
  );
};

export default CookiePolicy;

